import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Typography, Space, Spin, Grid, Tooltip, Switch } from "antd";
import { ArrowLeftOutlined, FilePdfOutlined, InfoCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import SearchListAutoComplete from "../../components/SearchListAutoComplete";
import ReportDom from "../../components/common/ReportDom";
import { salesReturnVouchersApi } from "../../apiservice/sales";

import SalesReturnVoucherBody from "./components/SalesReturnVoucherBody";
import { downloadPdf } from "../../lib/functions";
import { useShareDocument } from "../../hooks/useShareDocument";
import ShareDocumentButton from "../../components/ShareButton/ShareDocumentButton";
import { getInstitutionApi } from "../../apiservice/setting";
import Swal from "sweetalert2";
import { NULL } from "sass";

const { Text } = Typography;

const SalesReturnVoucher = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [vouchersList, setVouchersList] = useState([]);
  const [voucherNo, setVoucherNo] = useState(null);
  const [institution, institutionSet] = useState(null);
  const [showStamp, setShowStamp] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [accName, setAccName] = useState(null); 
  const [uploading, setUploading] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");  

  const {
  shareOpen,
  setShareOpen,
  email,
  setEmail,
  // loading,
  handleEmailShare,
  handleWhatsAppShare,
} = useShareDocument();  
  

  useEffect(() => {
    const fetchVouchers = async () => {
      setLoading(true);
      try {
        const res = await salesReturnVouchersApi({}); // fetch all vouchers
        setVouchersList(res.data || []);
      } catch {
        setVouchersList([]);
      } finally {
        setLoading(false);
      }
    };
    let getInstitution = async () => {
      await getInstitutionApi()
        .then((res) => {
          
          institutionSet(res.data);
        })
        .catch((err) => {
          if (err.response?.data?.message) {
    
            Swal({ 
                title: err.response.data.message, 
                icon: "warning" 
            });
        } else {
            Swal({ 
                title: "Internal Error",  text: "If the issue persists, please contact our support team.", 
                icon: "warning" 
            });
        }
        });
    };
    fetchVouchers();
    getInstitution();
  }, []);

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Sales Return Voucher-${voucherNo ? voucherNo : ""}`,
    removeAfterPrint: true,
  });

   useEffect(() => {
      if (shareOpen && customerEmail && !email) {
        setEmail(customerEmail);
      }
    }, [shareOpen, customerEmail, email, setEmail]);

  return (
    <div className="p-4">
      {/* Header Bar */}
      {/* Header Bar */}
        <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            gap: 8,
        }}
        >
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/sales-dashboard")}>
            {!screens.xs && "Back"}
        </Button>

        <div style={{ flex: 1, maxWidth: screens.xs ? 200 : 400 }}>
            <SearchListAutoComplete
            entityType="voucher"
            placeholder="Search Voucher No"
            optionsList={(vouchersList || []).map((v) => ({
                sale_id: v.sale_r_id,
                display_text: v.display_text,
                raw: v,
            }))}
            style={{alignItems:"center"}}
            onSelect={(obj) => setSelectedVoucher(obj?.raw || null)}
            showAddNew={false}
            />
        </div>
        <Space align="center">
          <Switch
            checked={showStamp}
            onChange={setShowStamp}
            checkedChildren="Stamp"
            unCheckedChildren="Stamp"
          />

          <Switch
            checked={showSignature}
            onChange={setShowSignature}
            checkedChildren="Signature"
            unCheckedChildren="Signature"
          />

          <ShareDocumentButton
          disabled={!selectedVoucher}
          screens={screens}
          shareOpen={shareOpen}
          setShareOpen={setShareOpen}
          email={email}
          setEmail={setEmail}
          loading={loading}
          onEmail={() =>
            handleEmailShare({
              url: `/print/sales-return/${selectedVoucher.sale_r_id}`,
              fileName: `SalesReturn_${voucherNo || selectedVoucher.display_text}.pdf`,
              name: accName || "",
              docNo: voucherNo || selectedVoucher.display_text,
            })
          }
          onWhatsApp={() =>
            handleWhatsAppShare({
              url: `/print/sales-return/${selectedVoucher.sale_r_id}`,
              fileName: `SalesReturn_${voucherNo || selectedVoucher.display_text}.pdf`,
              text: `Hello ${accName || ""}, Your Invoice ${voucherNo || selectedVoucher.display_text} is ready. Download here:`,
            })
          }
        />

        <Button
        type="primary"
        icon={<FilePdfOutlined />}
        disabled={!selectedVoucher || !voucherNo || pdfLoading }
        loading={pdfLoading}
        onClick={async () => {
          try {
          setPdfLoading(true);
          await downloadPdf(
            `/print/sales-return/${selectedVoucher.sale_r_id}`,
            `SalesReturn_${voucherNo || selectedVoucher.display_text}.pdf`
          )
          } catch (err) {
            console.error(err);
          } finally {
            setPdfLoading(false);
          }
        }
      }
      >
        {!screens.xs && "Download"}
      </Button>


        <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            disabled={!selectedVoucher}
        >
            { !screens.xs && "Print" }
        </Button>
        <Tooltip
            placement="bottom"
            title={
              <div style={{ maxWidth: 260 }}>
                <strong>Print Tip</strong>
                <div style={{ marginTop: 6 }}>
                  For best results, please disable
                  <b> “Headers and Footers”</b> in your browser’s print settings.
                </div>
              </div>
            }
          >
            <InfoCircleOutlined
              style={{
                color: "#888",
                fontSize: 16,
                cursor: "pointer",
              }}
            />
          </Tooltip>
          </Space>
        </div>


      {/* Voucher Display */}
      <Card className="shadow-md rounded-xl">
        {!selectedVoucher ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            {loading ? (
              <Spin />
            ) : (
              <Text type="secondary">Search and select a voucher</Text>
            )}
          </div>
        ) : (
          <SalesReturnVoucherBody sale_r_id={selectedVoucher.sale_r_id} setVoucherNo={setVoucherNo} setAccName={setAccName} institution={institution} showStamp={showStamp} showSignature={showSignature} setCustomerEmail={setCustomerEmail}/>
        )}
      </Card>

      {/* Hidden print layout */}
      {selectedVoucher && (
        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <ReportDom
            ref={reportRef}
            mode={showSignature ? "signatureOn" : showStamp ? "signatureOn" : ""}
            // title="Sales Return Voucher"
            renderTable={() => (
              <SalesReturnVoucherBody sale_r_id={selectedVoucher.sale_r_id} institution={institution} printMode showStamp={showStamp} showSignature={showSignature} />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default SalesReturnVoucher;
